import { Link } from 'react-router-dom';
import { Music, Users, Calendar, MapPin, ArrowRight, Star, Sparkles } from 'lucide-react';
import { SEOHead } from '../SEOHead';
import { Headline } from '../ui/Headline';
import ParticleCanvas from '../ui/ParticleCanvas';

interface CTAButton {
  text: string;
  link: string;
  primary?: boolean;
}

interface Feature {
  icon: typeof Music;
  title: string;
  description: string;
  link: string;
}

interface Testimonial {
  quote: string;
  author: string;
  role: string;
}

interface SEOLandingPageProps {
  title: string;
  description: string;
  h1: string;
  heroText: string;
  ctaButtons: CTAButton[];
  features: Feature[];
  teaserSection?: {
    title: string;
    description: string;
    items: string[];
  };
  seoTeaser?: {
    title: string;
    description: string;
  };
  faqItems?: { question: string; answer: string }[];
  keywords?: string[];
  locationName?: string;
}

export default function SEOLandingPage({
  title,
  description,
  h1,
  heroText,
  ctaButtons,
  features,
  teaserSection,
  seoTeaser,
  faqItems,
  keywords,
  locationName,
}: SEOLandingPageProps) {
  return (
    <>
      <SEOHead
        title={title}
        description={description}
        keywords={keywords}
      />

      <section className="gradient-bg relative">
        <ParticleCanvas className="absolute bottom-0 left-0 top-0 right-0" />
        <div className="container mx-auto px-6 my-8">
          <div className="max-w-6xl flex flex-col justify-center">
            <h1 className="font-tilt-warp text-4xl md:text-5xl lg:text-7xl leading-extra-tight light mb-12">
              {h1}
            </h1>
            <p className="text-xl md:text-2xl leading-relaxed mb-12">
              {heroText}
            </p>
            <div className="flex gap-4 flex-wrap">
              {ctaButtons.map((button, index) => (
                <Link
                  key={index}
                  to={button.link}
                  className={`btn text-lg px-8 py-4 inline-flex items-center group w-fit ${
                    button.primary
                      ? 'bg-dark hover:bg-dark/80'
                      : 'hover:bg-dark/80'
                  }`}
                >
                  {button.text}
                  <ArrowRight className="ml-2 w-5 h-5 transform transition-transform group-hover:translate-x-1" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

       {seoTeaser && (
            <section className="max-w-5xl mx-auto pt-24">
              <div className="bg-gray-custom/10 backdrop-blur-sm rounded-xl shadow-lg p-8 md:p-12">
                <h2 className="font-tilt-warp text-3xl md:text-4xl text-light mb-6">
                  {seoTeaser.title}
                </h2>
                <p className="text-light/70 text-lg leading-relaxed">
                  {seoTeaser.description}
                </p>
              </div>
            </section>
          )}

      <div className="container mx-auto px-6 pb-8 pt-24 md:py-24">
        <div className="space-y-24">
          <section>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <Link
                    key={index}
                    to={feature.link}
                    className="bg-gray-custom/10 backdrop-blur-sm rounded-xl p-8 hover:bg-gray-custom/20 transition-all group flex flex-col items-center"
                  >
                    <div className="bg-tertiary w-14 h-14 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <Icon className="w-7 h-7 text-light" />
                    </div>
                    <h2 className="text-xl font-bold text-light mb-3">{feature.title}</h2>
                    <p className="text-light/60 leading-relaxed">{feature.description}</p>
                  </Link>
                );
              })}
            </div>
          </section>

          {teaserSection && (
            <>
              <section className="max-w-3xl mx-auto">
                <div className="bg-gray-custom/10 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden">
                  <div className="p-8 md:p-12">
                    <h2 className="font-tilt-warp text-3xl md:text-4xl text-light mb-6">
                      {teaserSection.title}
                    </h2>
                    <p className="text-light/70 text-lg mb-8 leading-relaxed">
                      {teaserSection.description}
                    </p>
                    <ul className="space-y-4">
                      {teaserSection.items.map((item, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-tertiary mr-3 mt-1">✓</span>
                          <span className="text-light/80">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>
              </>
          )}

              <section className="max-w-5xl mx-auto">
                <div className="bg-primary rounded-xl shadow-2xl p-8 md:p-12 text-center">
                  <h2 className="font-tilt-warp text-3xl md:text-4xl text-light mb-6">
                    jamnation steckt noch in den Startlöchern
                  </h2>
                  <p className="text-light/90 text-lg md:text-xl leading-relaxed max-w-3xl mx-auto">
                    ... und du kannst von Anfang an dabei sein! Melde dich jetzt kostenlos an, erstelle dein Profil und werde Teil einer wachsenden Community von leidenschaftlichen Musikern. Je mehr wir sind, desto stärker wird unser Netzwerk. Erzähl deinen Bandkollegen, Musikerfreunden und der ganzen Szene von jamnation. Gemeinsam machen wir die Schweizer Musikszene sichtbarer, vernetzter und lebendiger. Sei von Anfang an dabei und präge die Zukunft der Schweizer Musiklandschaft mit!
                  </p>
                  <div className="mt-8 flex gap-4 justify-center flex-wrap">
                    <Link
                      to="/register"
                      className="btn bg-dark hover:bg-dark/80 text-lg px-8 py-4 inline-flex items-center group"
                    >
                      Jetzt kostenlos registrieren
                      <ArrowRight className="ml-2 w-5 h-5 transform transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>
                </div>
              </section>

          {faqItems && faqItems.length > 0 && (
            <section>
              <div className="max-w-3xl mx-auto">
                <Headline title="Häufig gestellte Fragen" size={3} />
              </div>
              <div className="max-w-3xl mx-auto space-y-6">
                {faqItems.map((item, index) => (
                  <div
                    key={index}
                    className="bg-gray-custom/10 backdrop-blur-sm rounded-xl p-6"
                  >
                    <h3 className="text-lg font-bold text-light mb-3">{item.question}</h3>
                    <p className="text-light/70 leading-relaxed">{item.answer}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {locationName && (
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-gray-custom/10 backdrop-blur-sm px-6 py-3 rounded-full border border-gray-custom/20">
                <MapPin className="w-5 h-5 text-primary" />
                <span className="text-light font-semibold">{locationName}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
